import ModalComponent from "../../../Components/ModalComponent";
import { Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../apis/store";
import { useMemo, useState } from "react";
import { toPrintInvoice } from "../../../apis/actions/appointments.actions";
import { useTranslation } from "react-i18next";
import {
  dedupeInvoiceLines,
  formatInvoiceDateDisplay,
  formatMoney,
  parseInvoiceLineLabel,
  resolveInvoiceDateRaw,
} from "../../../utils/invoiceDisplay";

interface MyInvoicesDetailsProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  selectedData: any;
  type?: string;
}

const MyInvoicesDetails = ({
  showModal,
  setShowModal,
  selectedData,
}: MyInvoicesDetailsProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingPrint, setLoadingPrint] = useState(false);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const font = isRtl ? " ac--font-expo" : "";

  const lines = useMemo(
    () => dedupeInvoiceLines(selectedData?.invoice_line_ids ?? []),
    [selectedData]
  );

  const invoiceDateDisplay = useMemo(
    () =>
      selectedData
        ? formatInvoiceDateDisplay(selectedData, i18n.language)
        : "—",
    [selectedData, i18n.language]
  );

  const dateFromLines = useMemo(() => {
    if (!selectedData) return false;
    const raw = selectedData.invoice_date;
    const hasApiDate =
      raw !== false && raw !== null && raw !== undefined && raw !== "";
    return !hasApiDate && Boolean(resolveInvoiceDateRaw(selectedData));
  }, [selectedData]);

  const customerName = useMemo(() => {
    for (const line of lines as { name?: string }[]) {
      const parsed = parseInvoiceLineLabel(line?.name ?? "");
      if (parsed.customer) return parsed.customer;
    }
    return null;
  }, [lines]);

  const handlePrint = async () => {
    if (!selectedData?.access_token) return;
    setLoadingPrint(true);
    try {
      await dispatch(
        toPrintInvoice({
          id: selectedData.access_token,
          lang: i18n.language,
        })
      );
    } finally {
      setLoadingPrint(false);
    }
  };

  if (!selectedData) return null;

  return (
    <ModalComponent
      title={null}
      open={showModal}
      onClose={() => setShowModal(false)}
      closeOnOutsideClick
      width={560}
      modalStyle={{ top: 120 }}
      showCloseIcon
    >
      <div className={`ac-invoice-detail-modal${font}`}>
        <header className="ac-invoice-detail-modal__header">
          <h2 className={`ac-invoice-detail-modal__title${font}`}>
            {t("profile.invoice.invoiceDetails.invoiceDetails")}
          </h2>
          <Button
            type="default"
            className={`ac-invoice-detail-modal__print${font}`}
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={loadingPrint}
            disabled={!selectedData?.access_token}
          >
            {t("profile.invoice.print")}
          </Button>
        </header>

        <section className="ac-invoice-detail-modal__summary" aria-label={t("profile.invoice.invoiceDetails.invoiceDetails")}>
          <div className="ac-invoice-detail-modal__summary-item">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.invoice.invoiceNumber")}
            </span>
            <span className={`ac-invoice-detail-modal__value${font}`}>
              {selectedData.name ?? "—"}
            </span>
          </div>
          <div className="ac-invoice-detail-modal__summary-item">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.invoice.invoiceDate")}
            </span>
            <span className={`ac-invoice-detail-modal__value${font}`}>
              {invoiceDateDisplay}
            </span>
            {dateFromLines ? (
              <p className={`ac-invoice-detail-modal__hint${font}`}>
                {t("profile.invoice.dateFromLines")}
              </p>
            ) : null}
          </div>
          <div className="ac-invoice-detail-modal__summary-item">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.invoice.company")}
            </span>
            <span className={`ac-invoice-detail-modal__value${font}`}>
              {selectedData.company_id || "—"}
            </span>
          </div>
          {customerName ? (
            <div className="ac-invoice-detail-modal__summary-item">
              <span className={`ac-invoice-detail-modal__label${font}`}>
                {t("profile.invoice.customer")}
              </span>
              <span className={`ac-invoice-detail-modal__value${font}`}>
                {customerName}
              </span>
            </div>
          ) : null}
          {selectedData.invoice_origin ? (
            <div className="ac-invoice-detail-modal__summary-item">
              <span className={`ac-invoice-detail-modal__label${font}`}>
                {t("profile.invoice.origin")}
              </span>
              <span className={`ac-invoice-detail-modal__value${font}`}>
                {selectedData.invoice_origin}
              </span>
            </div>
          ) : null}
          <div className="ac-invoice-detail-modal__summary-item ac-invoice-detail-modal__summary-item--total">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.invoice.total")} ({t("profile.order.cart.vat")})
            </span>
            <span
              className={`ac-invoice-detail-modal__value ac-invoice-detail-modal__value--total icon-saudi_riyal${font}`}
            >
              {formatMoney(selectedData.amount_total)}
            </span>
          </div>
        </section>

        {/* Line items — hidden until ERP returns meaningful line prices
        <section>
          <h3 className={`ac-invoice-detail-modal__section-title${font}`}>
            {t("profile.invoice.lineItems")}
          </h3>
          {lines.length === 0 ? (
            <p className={`ac-invoice-detail-modal__empty${font}`}>
              {t("profile.invoice.noLines")}
            </p>
          ) : (
            <ul className="ac-invoice-detail-modal__lines">
              {lines.map((line: any) => {
                const parsed = parseInvoiceLineLabel(line?.name ?? "");
                return (
                  <li key={line.id} className="ac-invoice-detail-modal__line">
                    <p className={`ac-invoice-detail-modal__line-title${font}`}>
                      {parsed.title || line.name}
                    </p>
                    <div className={`ac-invoice-detail-modal__line-meta${font}`}>
                      {parsed.customer ? (
                        <span>
                          {t("profile.invoice.customer")}: {parsed.customer}
                        </span>
                      ) : null}
                      {parsed.date ? (
                        <span>
                          {t("profile.invoice.invoiceDate")}: {parsed.date}
                        </span>
                      ) : null}
                      <span>
                        {t("invoice.quantity")}: {line.quantity ?? 1}
                      </span>
                    </div>
                    <div className={`ac-invoice-detail-modal__line-amounts${font}`}>
                      <span>
                        {t("profile.invoice.unitPrice")}:{" "}
                        <span className="icon-saudi_riyal">
                          {formatMoney(line.price_unit)}
                        </span>
                      </span>
                      <span>
                        {t("profile.invoice.subtotal")}:{" "}
                        <span className="icon-saudi_riyal">
                          {formatMoney(line.price_subtotal)}
                        </span>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        */}

        {selectedData.donation_amount > 0 ? (
          <div className={`ac-invoice-detail-modal__donation${font}`}>
            <span>{t("profile.order.cart.DonationAmount")}:</span>
            <span className="icon-saudi_riyal">
              {formatMoney(selectedData.donation_amount)}
            </span>
          </div>
        ) : null}
      </div>
    </ModalComponent>
  );
};

export default MyInvoicesDetails;
